import React, { useMemo, useState, useCallback } from 'react';
import {
  StyleSheet,
  ScrollView,
  Pressable,
  View,
  Platform,
  useWindowDimensions,
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { Redirect } from 'expo-router';
import { useTheme } from '@/context/ThemeContext';
import { isDarkTheme } from '@/constants/Colors';
import { useModalStore, useAuthStore } from '@/store';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';
import useResponsiveness from '@/hooks/useResponsiveness';
import AnnouncementBanner from '@/components/Banner/AnnouncementBanner';
import PromoBanner from '@/components/Banner/PromoBanner';
import BannerModal from '@/components/Banner/BannerModal';
import BannerCountdown from '@/components/Banner/BannerCountdown';
import TooltipBanner from '@/components/Banner/TooltipBanner';
import OnboardingFlow from '@/components/Onboarding/OnboardingFlow';

const TABS = ['Modals', 'Banners', 'Onboarding'];

// ─── Modal tab data ────────────────────────────────────────────────

const MODAL_NAMES = [
  'GhostSignupModal',
  'LoginModal',
  'SignupModal',
  'PricingModal',
  'CreditsPurchaseModal',
  'CreditsRequiredModal',
  'CreditsInfoModal',
  'OutOfCreditsModal',
  'ModelAccessPaywallModal',
  'QuickPurchaseConfirmModal',
  'AutoTopUpPromptModal',
  'AutoTopUpConfigModal',
  'SettingsModalWeb',
  'SettingsModalMobile',
  'AgentDetailsModal',
  'CustomAgentModal',
  'CancelPurchaseModal',
  'InputModal',
  'ConfirmationModal',
  'ShareModal',
  'ChatDetailsModal',
  'CollectionCustomInstructionsModal',
  'CollectionMemorySettingsModal',
  'CollectionFilesModal',
  'CollectionsIconGridModal',
  'CreateCollectionModal',
  'FileUploadModal',
  'FileEditModal',
  'FilesModal',
  'MoveToCollectionModal',
  'MoveChatsToCollectionModal',
  'AddFilesToCollectionModal',
  'FeedbackModal',
  'ReportModal',
  'ModelDetailsModal',
  'ModelSelectorDetailedModal',
  'CreateTaskModal',
  'SetupTaskFromTemplateModal',
  'CreateMemoryModal',
  'MemoryInfoModal',
  'MemoryBuilderModal',
  'ChatInputCustomParametersModal',
  'ComposerToolsMenu',
  'CoworkerComingSoonModal',
  'MusicLyricsModal',
  'ImagePromptModal',
  'VideoPromptModal',
  'WebSearchModal',
  'SaveImageInstructionsModal',
  'VoiceCreditsDepletedModal',
  'VoiceMediaErrorModal',
  'VoiceVisionPermissionModal',
  'OnboardingCompleteModal',
  'CreateAPIKeyModal',
  'InstallAppModal',
  'DownloadAppsModal',
  'ConversationOutputsModal',
  'ConversationSelectModal',
  'SubscriptionExtendModal',
  'ImportMemoryModal',
  'CancelSubscriptionGuideModal',
  'SubscriptionCheckoutModal',
].sort((a, b) => a.localeCompare(b));

function getDummyProps(modalName) {
  const noop = () => {};
  const base = { onClose: noop };

  switch (modalName) {
    case 'AgentDetailsModal':
      return { ...base, agentId: 'test-agent-id' };
    case 'CustomAgentModal':
      return { ...base, agentId: null };
    case 'ChatDetailsModal':
      return { ...base, conversationId: 'test-conversation-id' };
    case 'ShareModal':
      return { ...base, conversationId: 'test-conversation-id' };
    case 'FileEditModal':
      return { ...base, fileId: 'test-file-id' };
    case 'ModelDetailsModal':
      return { ...base, modelId: 'test-model-id' };
    case 'CollectionFilesModal':
      return {
        ...base,
        collectionId: 'test-collection-id',
        collectionTitle: 'Test Collection',
        files: [],
        handleFileUpload: noop,
        handleFileDelete: noop,
        onFilesAdded: noop,
      };
    case 'CollectionCustomInstructionsModal':
      return { ...base, collectionId: 'test-collection-id', initialInstructions: '', onSave: noop };
    case 'CollectionMemorySettingsModal':
      return { ...base, collectionId: 'test-collection-id', collectionTitle: 'Test', initialMemoryMode: 'account', onSaveMode: noop };
    case 'ConversationOutputsModal':
      return {
        ...base,
        conversationId: 'test-conversation-id',
        outputs: [
          { id: 'jump', file: { name: 'jump.wav', fileType: 'audio', source: 'inference', size: 48000 } },
          { id: 'coin', file: { name: 'coin-collect.wav', fileType: 'audio', source: 'inference', size: 36000 } },
        ],
      };
    case 'ConversationSelectModal':
      return { ...base, value: null, onSelect: noop };
    case 'SubscriptionCheckoutModal':
      return { ...base, checkoutUrl: 'https://example.com/checkout' };
    case 'SetupTaskFromTemplateModal':
      return {
        ...base,
        template: {
          id: 'test-template-id',
          name: 'Test Task Template',
          configurableFields: [],
        },
        onSuccess: noop,
      };
    case 'MusicLyricsModal':
      return {
        ...base,
        styleData: {
          id: 'test-style-id',
          name: 'Test Style',
          promptTemplate: 'Write lyrics about {{topic}}',
          exampleLyrics: 'Sample lyrics...',
        },
      };
    case 'ReportModal':
      return { ...base, isOpen: true, conversationId: 'test-conversation-id', messageId: null };
    case 'FeedbackModal':
      return { ...base, isOpen: true };
    case 'CancelPurchaseModal':
      return { ...base, onConfirm: noop };
    case 'InputModal':
      return { ...base, title: 'Test input', placeholder: 'Enter value', onSubmit: noop };
    case 'ConfirmationModal':
      return {
        ...base,
        title: 'Confirm',
        message: 'Are you sure?',
        confirmLabel: 'Confirm',
        onConfirm: noop,
      };
    case 'AddFilesToCollectionModal':
      return { ...base, collectionId: 'test-collection-id', fileIds: [] };
    case 'MoveToCollectionModal':
      return { ...base, fileIds: ['test-file-id'], onSuccess: noop };
    case 'MoveChatsToCollectionModal':
      return { ...base, chatIds: ['test-chat-id'], excludeCollectionId: 'test-collection-id', onSuccess: noop };
    case 'ModelSelectorDetailedModal':
      return { ...base, modelIds: [], onChange: noop };
    case 'CreateTaskModal':
      return { ...base, onSuccess: noop };
    case 'CreateMemoryModal':
      return { ...base, initialContent: '', onSuccess: noop };
    case 'CreateCollectionModal':
      return { ...base, onSuccess: noop };
    case 'CreateAPIKeyModal':
      return { ...base, onKeyCreated: noop };
    case 'CollectionsIconGridModal':
      return { ...base, selectedIcon: null, onIconSelect: noop };
    case 'CreditsRequiredModal':
      return { ...base, requestCost: 18, holdCost: 27 };
    case 'SettingsModalWeb':
      return { ...base, tab: 'appearance' };
    case 'SettingsModalMobile':
      return { ...base, initialScreen: 'main' };
    case 'CreditsPurchaseModal':
      return { ...base, initialTab: 'buy-credits' };
    case 'FilesModal':
      return { ...base, onSelect: noop };
    case 'FileUploadModal':
      return { ...base, files: [], onUpload: noop };
    case 'GhostSignupModal':
      return { ...base, title: 'Ready to unlock?', subtitle: 'Join for free.' };
    case 'OutOfCreditsModal':
      return { ...base, title: 'Out of credits', subtitle: 'Get more credits to continue.' };
    case 'ModelAccessPaywallModal':
      return { ...base, modelName: 'Claude 4 Opus', title: 'Unlock Claude 4 Opus', subtitle: 'To use this model, buy credits or upgrade to Plus.' };
    default:
      return base;
  }
}

// ─── Banner tab data ───────────────────────────────────────────────

const COUNTDOWN_ENDS_AT = new Date(Date.now() + 2 * 60 * 60 * 1000 + 15 * 60 * 1000).toISOString();

const EXAMPLE_ANNOUNCEMENT_BANNERS = [
  {
    id: 'demo-announce-promo',
    contentType: 'promo-code',
    placement: 'home-banner',
    isDismissible: true,
    content: {
      icon: 'gift',
      headline: '50% off your first month!',
      subtitle: 'Use code below at checkout',
      badgeText: 'LIMITED TIME',
      ctaText: 'Claim Offer',
      ctaUrl: 'modal:PricingModal',
    },
    promoCode: { code: 'WELCOME50' },
    countdown: { enabled: true, endsAt: COUNTDOWN_ENDS_AT },
  },
  {
    id: 'demo-announce-feature',
    contentType: 'feature',
    placement: 'home-banner',
    isDismissible: true,
    content: {
      icon: 'sparkles',
      headline: 'New: Image Generation is here!',
      subtitle: 'Create stunning images with AI',
      ctaText: 'Try it now',
      ctaUrl: '/app',
    },
    promoCode: null,
    countdown: null,
  },
  {
    id: 'demo-announce-announcement',
    contentType: 'announcement',
    placement: 'home-banner',
    isDismissible: false,
    content: {
      icon: 'bullhorn',
      headline: 'Scheduled maintenance tonight at 11 PM EST',
      subtitle: 'Expected downtime: 30 minutes',
    },
    promoCode: null,
    countdown: null,
  },
  {
    id: 'demo-announce-link',
    contentType: 'link',
    placement: 'home-banner',
    isDismissible: true,
    content: {
      icon: 'newspaper',
      headline: 'Read our latest blog post',
      badgeText: 'NEW',
      ctaText: 'Read More',
      ctaUrl: 'https://example.com/blog',
    },
    promoCode: null,
    countdown: null,
  },
];

const EXAMPLE_PROMO_BANNERS = [
  {
    id: 'demo-promo-code',
    contentType: 'promo-code',
    placement: 'sidebar',
    isDismissible: true,
    content: {
      badgeText: 'EXCLUSIVE',
      headline: 'Get 30% off Plus',
      subtitle: 'Upgrade now and save with this promo code.',
      ctaText: 'Upgrade',
      ctaUrl: 'modal:PricingModal',
      promoAutoApply: true,
    },
    promoCode: { code: 'PLUS30' },
    countdown: { enabled: true, endsAt: COUNTDOWN_ENDS_AT },
  },
  {
    id: 'demo-promo-feature',
    contentType: 'feature',
    placement: 'sidebar',
    isDismissible: true,
    content: {
      headline: 'Voice mode now available',
      subtitle: 'Talk to your AI assistant naturally.',
      ctaText: 'Learn more',
      ctaUrl: '/app',
    },
    promoCode: null,
    countdown: null,
  },
  {
    id: 'demo-promo-announce',
    contentType: 'announcement',
    placement: 'sidebar',
    isDismissible: true,
    content: {
      badgeText: 'UPDATE',
      headline: 'Memory is now smarter',
      subtitle: 'Your assistant remembers more context across sessions.',
    },
    promoCode: null,
    countdown: null,
  },
  {
    id: 'demo-promo-link',
    contentType: 'link',
    placement: 'sidebar',
    isDismissible: true,
    content: {
      headline: 'Check out the API docs',
      subtitle: 'Build integrations with our REST API.',
      ctaText: 'View Docs',
      ctaUrl: 'https://example.com/docs',
    },
    promoCode: null,
    countdown: null,
  },
];

const EXAMPLE_MODAL_BANNERS = [
  {
    id: 'demo-modal-promo',
    contentType: 'promo-code',
    placement: 'modal',
    isDismissible: true,
    content: {
      badgeText: 'FLASH SALE',
      headline: 'Don\'t miss out!',
      subtitle: 'Get 40% off annual plans. This offer expires soon — upgrade now and lock in your savings.',
      ctaText: 'Claim Discount',
      ctaUrl: 'modal:PricingModal',
      promoAutoApply: true,
    },
    promoCode: { code: 'FLASH40' },
    countdown: { enabled: true, endsAt: COUNTDOWN_ENDS_AT },
  },
  {
    id: 'demo-modal-feature',
    contentType: 'feature',
    placement: 'modal',
    isDismissible: true,
    content: {
      badgeText: 'NEW FEATURE',
      headline: 'Collections are here!',
      subtitle: 'Organize your conversations and files into custom collections. Stay on top of everything.',
      ctaText: 'Explore Collections',
      ctaUrl: '/app',
    },
    promoCode: null,
    countdown: null,
  },
];

const EXAMPLE_TOOLTIP_BANNERS = [
  {
    id: 'demo-tooltip-feature',
    contentType: 'feature',
    content: {
      icon: 'lightbulb',
      headline: 'Try Custom Agents',
      subtitle: 'Create a personalized AI agent tailored to your workflow.',
      ctaText: 'Create Agent',
    },
    isDismissible: true,
    anchorLabel: 'Sidebar Button',
  },
  {
    id: 'demo-tooltip-promo',
    contentType: 'promo-code',
    content: {
      icon: 'bolt',
      headline: 'Running low on credits',
      subtitle: 'Top up now and get 20% bonus credits on your next purchase.',
      ctaText: 'Get Credits',
    },
    isDismissible: true,
    anchorLabel: 'Credits Counter',
  },
  {
    id: 'demo-tooltip-announcement',
    contentType: 'announcement',
    content: {
      icon: 'bullhorn',
      headline: 'New keyboard shortcuts',
      subtitle: 'Press ? to see all available shortcuts.',
    },
    isDismissible: true,
    anchorLabel: 'Help Icon',
  },
  {
    id: 'demo-tooltip-link',
    contentType: 'link',
    content: {
      icon: 'link',
      headline: 'Check out our API docs',
      subtitle: 'Build powerful integrations with our REST API.',
      ctaText: 'View Docs',
    },
    isDismissible: true,
    anchorLabel: 'Docs Link',
  },
];


// ─── Sub-components ────────────────────────────────────────────────

function ModalsList({ styles, openModal }) {
  return (
    <>
      <Text size="n" color="secondary" style={styles.subtitle}>
        Tap a modal name to open it. Uses dummy data where needed.
      </Text>
      <View style={styles.list}>
        {MODAL_NAMES.map((name) => (
          <Pressable
            key={name}
            onPress={() => openModal(name, getDummyProps(name))}
            style={({ pressed }) => [styles.item, pressed && styles.itemPressed]}
            accessibilityRole="button"
            accessibilityLabel={`Open ${name}`}
          >
            <Text style={styles.itemText} numberOfLines={1}>
              {name}
            </Text>
          </Pressable>
        ))}
      </View>
    </>
  );
}

function BannerSection({ label, children, styles }) {
  return (
    <View style={styles.bannerSection}>
      <Text size="m" weight="bold" color="primary" style={styles.bannerSectionTitle}>
        {label}
      </Text>
      {children}
    </View>
  );
}

function TooltipBannerDemo({ styles, dismissedIds, onDismiss }) {
  const { colors, theme } = useTheme();
  const isDark = isDarkTheme(theme);

  return (
    <BannerSection label="TooltipBanner" styles={styles}>
      <Text size="s" color="tertiary" style={{ marginBottom: 8 }}>
        Real TooltipBanner component — one per content type (feature, promo-code, announcement, link).
      </Text>
      <View style={styles.bannerList}>
        {EXAMPLE_TOOLTIP_BANNERS.map((banner) =>
          !dismissedIds.has(banner.id) ? (
            <View key={banner.id} style={{ position: 'relative', minHeight: 140, marginBottom: 12 }}>
              <View style={{
                paddingVertical: 8,
                paddingHorizontal: 16,
                borderRadius: 8,
                backgroundColor: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.05)',
                borderWidth: 1,
                borderColor: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)',
                borderStyle: 'dashed',
                alignSelf: 'flex-end',
                marginRight: 20,
              }}>
                <Text size="xs" color="tertiary">{banner.anchorLabel} ({banner.contentType})</Text>
              </View>
              <TooltipBanner
                banner={banner}
                position="below"
                onDismiss={onDismiss}
              />
            </View>
          ) : null
        )}
      </View>
    </BannerSection>
  );
}

function BannersList({ styles }) {
  const [dismissedIds, setDismissedIds] = useState(new Set());
  const [activeModalBanner, setActiveModalBanner] = useState(null);

  const noop = useCallback(() => {}, []);
  const handleDismiss = useCallback((id) => {
    setDismissedIds(prev => new Set(prev).add(id));
  }, []);

  const resetAll = useCallback(() => {
    setDismissedIds(new Set());
    setActiveModalBanner(null);
  }, []);

  const isVisible = (id) => !dismissedIds.has(id);

  return (
    <>
      <Text size="n" color="secondary" style={styles.subtitle}>
        All banner components rendered with example data. Dismiss to test animations, then reset.
      </Text>

      <Pressable
        onPress={resetAll}
        style={({ pressed }) => [styles.resetBtn, pressed && styles.itemPressed]}
      >
        <Text size="s" weight="bold" style={{ color: '#fff' }}>
          Reset All Banners
        </Text>
      </Pressable>

      {/* AnnouncementBanner */}
      <BannerSection label="AnnouncementBanner" styles={styles}>
        <Text size="s" color="tertiary" style={{ marginBottom: 8 }}>
          Toast-style announcement bar — used for home-banner and chat-banner placements.
        </Text>
        <View style={styles.bannerList}>
          {EXAMPLE_ANNOUNCEMENT_BANNERS.map((banner) =>
            isVisible(banner.id) ? (
              <AnnouncementBanner
                key={banner.id}
                banner={banner}
                onMarkSeen={noop}
                onDismiss={handleDismiss}
              />
            ) : null
          )}
        </View>
      </BannerSection>

      {/* PromoBanner */}
      <BannerSection label="PromoBanner" styles={styles}>
        <Text size="s" color="tertiary" style={{ marginBottom: 8 }}>
          Card-style inline banner — used for sidebar, credit store, and similar placements.
        </Text>
        <View style={styles.bannerList}>
          {EXAMPLE_PROMO_BANNERS.map((banner) =>
            isVisible(banner.id) ? (
              <PromoBanner
                key={banner.id}
                banner={banner}
                onMarkSeen={noop}
                onDismiss={handleDismiss}
              />
            ) : null
          )}
        </View>
      </BannerSection>

      {/* PromoBanner compact */}
      <BannerSection label="PromoBanner (compact)" styles={styles}>
        <Text size="s" color="tertiary" style={{ marginBottom: 8 }}>
          Same component with isCompact=true.
        </Text>
        <View style={styles.bannerList}>
          {EXAMPLE_PROMO_BANNERS.slice(0, 2).map((banner) => {
            const compactId = banner.id + '-compact';
            return isVisible(compactId) ? (
              <PromoBanner
                key={compactId}
                banner={{ ...banner, id: compactId }}
                onMarkSeen={noop}
                onDismiss={handleDismiss}
                isCompact
              />
            ) : null;
          })}
        </View>
      </BannerSection>

      {/* BannerModal */}
      <BannerSection label="BannerModal" styles={styles}>
        <Text size="s" color="tertiary" style={{ marginBottom: 8 }}>
          Full-screen modal overlay — used for high-priority announcements.
        </Text>
        <View style={styles.bannerList}>
          {EXAMPLE_MODAL_BANNERS.map((banner) => (
            <Pressable
              key={banner.id}
              onPress={() => setActiveModalBanner(banner)}
              style={({ pressed }) => [styles.item, pressed && styles.itemPressed]}
            >
              <Text style={styles.itemText} numberOfLines={1}>
                {banner.contentType}: {banner.content.headline}
              </Text>
            </Pressable>
          ))}
        </View>
      </BannerSection>

      {activeModalBanner && (
        <BannerModal
          banner={activeModalBanner}
          onMarkSeen={noop}
          onDismiss={() => setActiveModalBanner(null)}
        />
      )}

      {/* TooltipBanner */}
      <TooltipBannerDemo styles={styles} dismissedIds={dismissedIds} onDismiss={handleDismiss} />

      {/* BannerCountdown */}
      <BannerSection label="BannerCountdown" styles={styles}>
        <Text size="s" color="tertiary" style={{ marginBottom: 8 }}>
          Standalone countdown timer used inside banners.
        </Text>
        <View style={[styles.item, { flexDirection: 'row', alignItems: 'center', gap: 10 }]}>
          <Text size="s" color="secondary">Ends in:</Text>
          <BannerCountdown
            endsAt={COUNTDOWN_ENDS_AT}
            onExpired={noop}
          />
        </View>
      </BannerSection>
    </>
  );
}

// ─── Onboarding tab data ──────────────────────────────────────────

const ONBOARDING_VARIANTS = [
  {
    id: 'work_intro_v3',
    label: 'Work intro (current)',
    component: OnboardingFlow,
    description: 'Welcome → About you → Chat vs Work → Plus + first action',
    screens: 4,
  },
];

function OnboardingList({ styles }) {
  const { colors, theme } = useTheme();
  const isDark = isDarkTheme(theme);
  const [activeVariant, setActiveVariant] = useState(null);

  const handleDismiss = useCallback(() => {
    setActiveVariant(null);
  }, []);

  const ActiveComponent = activeVariant?.component;

  return (
    <>
      <Text size="n" color="secondary" style={styles.subtitle}>
        Tap a variant to launch it full-screen. Navigate through screens using the built-in controls, then complete or skip to dismiss.
      </Text>

      <View style={styles.list}>
        {ONBOARDING_VARIANTS.map((variant) => (
          <Pressable
            key={variant.id}
            onPress={() => setActiveVariant(variant)}
            style={({ pressed }) => [styles.item, pressed && styles.itemPressed]}
            accessibilityRole="button"
            accessibilityLabel={`Launch ${variant.label} onboarding`}
          >
            <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}>
              <View style={{ flex: 1, gap: 2 }}>
                <Text style={styles.itemText} weight="semibold">
                  {variant.label}
                </Text>
                <Text size="xs" color="tertiary">
                  {variant.screens} screens — {variant.description}
                </Text>
              </View>
              <Icon name="arrow-up-right-from-square" size={13} color={colors.text_color_tertiary} />
            </View>
          </Pressable>
        ))}
      </View>

      {ActiveComponent && (
        <ActiveComponent
          onComplete={handleDismiss}
          onSkip={handleDismiss}
        />
      )}
    </>
  );
}

// ─── Main screen ───────────────────────────────────────────────────

export default function ModalTestScreen() {
  const isAdmin = useAuthStore(state => state.user?.flags?.isAdmin);
  const { colors } = useTheme();
  const { openModal } = useModalStore(state => state.actions);
  const { isXS } = useResponsiveness();
  const { width } = useWindowDimensions();
  const [activeTab, setActiveTab] = useState('Modals');

  if (!isAdmin) {
    return <Redirect href="/app" />;
  }

  const styles = useMemo(
    () =>
      StyleSheet.create({
        container: {
          flex: 1,
          backgroundColor: colors.solid_color_primary,
        },
        content: {
          paddingHorizontal: isXS ? 16 : 24,
          paddingTop: 16,
          paddingBottom: 32,
          maxWidth: 1000,
          flexGrow: 1,
          ...(Platform.OS === 'web' && width > 600 ? { alignSelf: 'center', width: '100%' } : {}),
        },
        title: {
          marginBottom: 12,
        },
        tabBar: {
          flexDirection: 'row',
          gap: 0,
          marginBottom: 20,
          borderBottomWidth: 1,
          borderBottomColor: colors.border_color_secondary,
        },
        tab: {
          paddingVertical: 10,
          paddingHorizontal: 18,
          borderBottomWidth: 2,
          borderBottomColor: 'transparent',
          ...(Platform.OS === 'web' ? { cursor: 'pointer' } : {}),
        },
        tabActive: {
          borderBottomColor: colors.text_color_primary,
        },
        tabText: {
          color: colors.text_color_tertiary,
          fontSize: 15,
          fontWeight: '500',
        },
        tabTextActive: {
          color: colors.text_color_primary,
          fontWeight: '700',
        },
        subtitle: {
          marginBottom: 20,
          opacity: 0.85,
        },
        list: {
          gap: 8,
        },
        item: {
          paddingVertical: 14,
          paddingHorizontal: 16,
          borderRadius: 10,
          backgroundColor: colors.solid_color_secondary,
          borderWidth: 1,
          borderColor: colors.border_color_secondary,
        },
        itemPressed: {
          opacity: 0.85,
        },
        itemText: {
          fontSize: 15,
          color: colors.text_color_primary,
        },
        bannerSection: {
          marginBottom: 28,
        },
        bannerSectionTitle: {
          marginBottom: 10,
          paddingBottom: 6,
          borderBottomWidth: 1,
          borderBottomColor: colors.border_color_secondary,
        },
        bannerList: {
          gap: 12,
        },
        resetBtn: {
          alignSelf: 'flex-start',
          paddingVertical: 8,
          paddingHorizontal: 16,
          borderRadius: 8,
          backgroundColor: colors.text_color_tertiary,
          marginBottom: 24,
          ...(Platform.OS === 'web' ? { cursor: 'pointer' } : {}),
        },
      }),
    [colors, isXS, width]
  );

  return (
    <SafeAreaView style={styles.container} edges={['left', 'right', 'top']}>
      <ScrollView
        style={styles.container}
        contentContainerStyle={styles.content}
        showsVerticalScrollIndicator={true}
        keyboardShouldPersistTaps="handled"
        bounces={true}
      >
        <Text size="xl" weight="bold" color="primary" style={styles.title}>
          Component Test
        </Text>

        <View style={styles.tabBar}>
          {TABS.map((tab) => (
            <Pressable
              key={tab}
              onPress={() => setActiveTab(tab)}
              style={[styles.tab, activeTab === tab && styles.tabActive]}
            >
              <Text style={[styles.tabText, activeTab === tab && styles.tabTextActive]}>
                {tab}
              </Text>
            </Pressable>
          ))}
        </View>

        {activeTab === 'Modals' && <ModalsList styles={styles} openModal={openModal} />}
        {activeTab === 'Banners' && <BannersList styles={styles} />}
        {activeTab === 'Onboarding' && <OnboardingList styles={styles} />}
      </ScrollView>
    </SafeAreaView>
  );
}
